import { useParams } from "react-router-dom";
import { auth } from "../../../firebase";
import { useEffect, useRef, useState } from "react";
import companiesQuery from "../../../firestore/Companies.query";
import { LinkMenus } from "../../../model/LinkMenus";
import TableList, { GridCol, GridRow } from "../../../components/table/TableList";
import Button from "../../../components/button/Button";
import { Spiner } from "../../../components/spinner/Spiner";
import { LinkMenusDeleteModal } from "./LinkMenusDeleteModal";
import { LinkMenusModal } from "./LinkMenusModal";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../../features/alert.slice";
import Badge from "../../../components/badges/Badges";

// 連携メニュー管理
export const LinkMenusPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [linkMenusList, setLinkMenusList] = useState<LinkMenus[]>([]);
  const [linkMenus, setLinkMenus] = useState<LinkMenus | undefined>();
  const [deleteLinkMenus, setDeleteLinkMenus] = useState<LinkMenus | undefined>();

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  const dispatch = useDispatch();

  const isDisabled = useRef(false);

  useEffect(() => {
    const f = async () => {
      // 連携メニュー一覧取得
      const linkMenusList = await companiesQuery.getLinkMenusList(tenantId!);
      setLinkMenusList(linkMenusList);

      setIsLoad(false);
    };
    f();
  }, [isOpen, dialogIsOpen, tenantId]);

  const columns: GridCol[] = [
    {
      field: "number",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "name",
      headerName: "名称",
      style: "",
    },
    {
      field: "type",
      headerName: "種別",
      style: "",
    },
    {
      field: "createType",
      headerName: "作成元",
      style: "",
    },
    {
      field: "control",
      headerName: "編集/削除",
      style: "w-2/12",
    },
  ];

  const rowList: GridRow[] = linkMenusList.map((linkMenus, index) => {
    const isAdmin = linkMenus.createType === undefined || linkMenus.createType === 'admin';
    return {
      rowData: {
        number: index + 1,
        name: (<div className={"sm:whitespace-normal"}>{linkMenus.name ?? ''}</div>),
        type: linkMenus.type === "App"
          ? (<Badge color="pink" children="アプリ" />)
          : (<Badge color="indigo" children="Web" />),
        createType: isAdmin ? '運営' : '団体',
        control: isAdmin
          ? (
            <div className="flex">
              <Button
                className="mr-2"
                onClick={() => {
                  setIsUpdate(true);
                  setLinkMenus(linkMenus);
                  toggle();
                }}
                children={"編集"}
              />
              <Button
                color="red"
                onClick={() => {
                  deleteDialogToggle();
                  setDeleteLinkMenus(linkMenus);
                }}
                children={"削除"}
              />
            </div>
          )
          : (<></>)
      },
      sortableRowData: {
        name: linkMenus.name ?? '',
        type: linkMenus.type === 'App',
        createType: isAdmin,
        control: isAdmin
      }
    };
  });

  const onSubmit = async (data: LinkMenus) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        const value: LinkMenus = {
          id: data?.id ?? '',
          type: data.type ?? "Web",
          name: data.name,
          packageName: '',
          bundleID: '',
          url: data.url ?? '',
          createType: data.createType ?? 'admin',
          createUser: data.createUser ?? currentUser?.email!,
          createAt: data?.createAt ?? new Date(),
        };

        if(isUpdate) {
          await companiesQuery.updateLinkMenus(tenantId!, value);
        } else {
          await companiesQuery.setLinkMenus(tenantId!, value);
        };
        dispatch(alertOpen(AlertValue("連携アプリを保存しました", "success")));
        toggle();
        isDisabled.current = false;
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        console.error(err);
        isDisabled.current = false;
      }
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const deleteDialogToggle = () => {
    setDialogIsOpen(!dialogIsOpen);
  };

  const onDelete = (linkMenus: LinkMenus) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        deleteDialogToggle();
        companiesQuery.deleteLinkMenus(tenantId!, linkMenus);
        companiesQuery.deleteMenuSettingByMenuNo(tenantId!, linkMenus.id);
        dispatch(alertOpen(AlertValue("削除しました。", "success")));
        isDisabled.current = false;
      } catch(err: any) {
        console.error(err);
        isDisabled.current = false;
      }
    }
  }

  return (
    <div className="h-full">
      <LinkMenusModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        linkMenus={linkMenus}
        authorEmail={currentUser?.email!}
        onSubmit={onSubmit}
      />
      <LinkMenusDeleteModal
        children={undefined}
        open={dialogIsOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        linkMenus={deleteLinkMenus}
        handle={onDelete}
      />
      <div className="flex items-center space-x-4 p-6 justify-between">
        <h2 className="flex text-xl whitespace-nowrap font-semibold text-gray-900">
          連携ボタンメニュー設定
        </h2>
        <div className={"flex space-x-2 items-center"}>
          <Button
            size={"sm"}
            onClick={() => {
              setIsUpdate(false);
              setLinkMenus(undefined);
              toggle();
            }}
            children={"連携ボタンメニュー新規作成"}
          />
        </div>
      </div>
      {isLoad === true ? <Spiner /> :(
        <div className={"px-6"}>
          <TableList
            header
            gridCol={columns}
            gridRow={rowList}
            defaultSortOrder={"desc"}
            limit={50}
          />
        </div>
      )}
    </div>
  );
}